// frontend/src/services/system.js
// System Service for Alertique Frontend
// Handles API calls for system health, status, and monitoring

import axios from "axios";
import { getApiBaseUrl } from "../config/api.js";
import { withSessionValidation, validateSessionForAdminCall } from "../utils/sessionValidation.js";
import { formatToLocalDateTime } from "../utils/timezoneUtils.js";

// Helper function for conditional debug logging
const debugLog = (...args) => {
  if (localStorage.getItem("debugMode") === "true") {
    console.log(...args);
  }
};

// Dynamic API URL from configuration
const getAPI_URL = () => getApiBaseUrl();

/**
 * Generates JWT Authentication headers from stored JWT token.
 * @returns {Object} An object containing the Authorization and Content-Type headers.
 * @returns {Object} An empty object if JWT token is not found in localStorage.
 */
const getJwtHeaders = () => {
  const token = localStorage.getItem("jwtToken");
  if (token) {
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }
  return {};
};

/**
 * Generates JWT Authentication headers from provided JWT token or stored token.
 * @param {string} jwtToken - Optional JWT token to use instead of stored token
 * @returns {Object} An object containing the Authorization and Content-Type headers.
 * @returns {Object} An empty object if JWT token is not found.
 */
const getJwtHeadersWithToken = (jwtToken = null) => {
  const token = jwtToken || localStorage.getItem("jwtToken");
  if (token) {
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }
  return {};
};

/**
 * DEPRECATED: Basic Auth with stored password is no longer supported for security reasons.
 * @returns {Object} Always returns empty object.
 */
const getAuthHeaders = () => {
  return {};
};

/**
 * Gets authentication headers using JWT only (password storage removed for security).
 * @returns {Object} Authentication headers object with JWT token if available.
 */
const getAuthHeadersWithJwt = () => {
  // Use JWT only (no password storage)
  return getJwtHeaders();
};

/**
 * Fetches health status from the backend API.
 * @returns {Promise<Object>} Health status data
 * @throws {Error} If request fails
 */
export const getHealthStatus = async () => {
  debugLog("system.js: Fetching health status"); // DEBUG
  debugLog("system.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    const response = await axios.get(`${getAPI_URL()}/health`);
    console.log(
      "system.js: Health status fetched successfully:",
      response.data
    ); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error fetching health status:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.message ||
          `Failed to fetch health status: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while fetching health status."
      );
    }
  }
};

/**
 * Fetches root endpoint information from the backend API.
 * @returns {Promise<Object>} Root endpoint data
 * @throws {Error} If request fails
 */
export const getRootInfo = async () => {
  debugLog("system.js: Fetching root info"); // DEBUG
  debugLog("system.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    const response = await axios.get(`${getAPI_URL()}/`);
    debugLog("system.js: Root info fetched successfully:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error fetching root info:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.message ||
          `Failed to fetch root info: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error("An unexpected error occurred while fetching root info.");
    }
  }
};

/**
 * Fetches scraper status with authentication.
 * @param {string} jwtToken - Optional JWT token to use instead of stored token
 * @returns {Promise<Object>} Scraper status data
 * @throws {Error} If request fails
 */
export const getScraperStatus = async (jwtToken = null) => {
  debugLog("system.js: Fetching scraper status"); // DEBUG
  debugLog("system.js: Using API URL:", getAPI_URL()); // DEBUG

  // Debug: Check if JWT token exists
  const token = jwtToken || localStorage.getItem("jwtToken");
  console.log("system.js: JWT token exists:", !!token); // DEBUG
  if (token) {
    console.log(
      "system.js: JWT token (first 20 chars):",
      token.substring(0, 20) + "..."
    ); // DEBUG
  }

  // Debug: Check what headers will be sent
  const headers = getJwtHeadersWithToken(jwtToken);
  debugLog("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.get(`${getAPI_URL()}/api/scrape/status`, {
      headers: headers, // Use JWT-aware headers
    });

    debugLog("system.js: Scraper status fetched successfully:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error fetching scraper status:", error); // DEBUG
    if (error.response) {
      console.error("system.js: Error response status:", error.response.status); // DEBUG
      console.error("system.js: Error response data:", error.response.data); // DEBUG
      throw new Error(
        error.response.data.error ||
          `Failed to fetch scraper status: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while fetching scraper status."
      );
    }
  }
};

/**
 * Checks if an item exists and gets its status.
 * @param {string} hmUrl - H&M URL of the item
 * @returns {Promise<Object>} Item status data
 * @throws {Error} If request fails
 */
export const getItemStatus = async (hmUrl) => {
  debugLog(`system.js: Checking item status for: ${hmUrl}`); // DEBUG
  debugLog("system.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    const response = await axios.get(`${getAPI_URL()}/api/item/status`, {
      params: { hm_url: hmUrl },
    });
    debugLog("system.js: Item status fetched successfully:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error fetching item status:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.message ||
          `Failed to fetch item status: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while fetching item status."
      );
    }
  }
};

/**
 * Formats health status for display.
 * @param {Object} health - Health object from API
 * @returns {Object} Formatted health info
 */
export const formatHealthStatus = (health) => {
  if (!health) return { status: "unknown", label: "Unknown", color: "gray" };

  const { status, version, environment, timestamp } = health;

  const statusConfig = {
    healthy: { label: "Healthy", color: "green" },
    unhealthy: { label: "Unhealthy", color: "red" },
    unknown: { label: "Unknown", color: "gray" },
  };

  const config = statusConfig[status] || statusConfig.unknown;

  return {
    status,
    label: config.label,
    color: config.color,
    version,
    environment,
    timestamp: timestamp ? formatToLocalDateTime(timestamp) : null,
  };
};

/**
 * Formats collection freshness for display.
 * @param {Object} freshness - Freshness object from API
 * @returns {Object} Formatted freshness info
 */
export const formatCollectionFreshness = (freshness) => {
  if (!freshness) return { status: "unknown", label: "Unknown", color: "gray" };

  const { status, age_hours, last_scanned_at } = freshness;

  const statusConfig = {
    very_fresh: { label: "Very Fresh", color: "green" },
    fresh: { label: "Fresh", color: "blue" },
    stale: { label: "Stale", color: "yellow" },
    very_stale: { label: "Very Stale", color: "red" },
    unknown: { label: "Unknown", color: "gray" },
  };

  const config = statusConfig[status] || statusConfig.unknown;

  return {
    status,
    label: config.label,
    color: config.color,
    ageHours: age_hours ? Math.round(age_hours) : null,
    ageLabel: age_hours ? `${Math.round(age_hours)}h ago` : null,
    lastScannedAt: last_scanned_at
      ? formatToLocalDateTime(last_scanned_at)
      : null,
  };
};

/**
 * Get current database mode (production or testing)
 * @returns {Promise<Object>} Database mode information
 * @throws {Error} If request fails
 */
export const getDatabaseMode = async (jwtToken = null) => {
  debugLog("system.js: Getting database mode"); // DEBUG
  debugLog("system.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    // Validate session before making admin API call
    return await withSessionValidation(async () => {
      const headers = getJwtHeadersWithToken(jwtToken);
      if (localStorage.getItem("debugMode") === "true") {
        console.log("system.js: Auth headers:", headers); // DEBUG
      }

      const response = await axios.get(`${getAPI_URL()}/api/database-mode`, {
        headers: headers,
      });

      if (localStorage.getItem("debugMode") === "true") {
        console.log(
          "system.js: Database mode fetched successfully:",
          response.data
        ); // DEBUG
      }
      return response.data;
    }, true); // requireAdmin = true
  } catch (error) {
    // Handle validation errors
    if (error.validationResult) {
      console.warn("system.js: Session validation failed:", error.validationResult);
      throw error;
    }

    // Handle other errors
    console.error("system.js: Error fetching database mode:", error); // DEBUG
    if (error.response) {
      console.error("system.js: Error response status:", error.response.status); // DEBUG
      console.error("system.js: Error response data:", error.response.data); // DEBUG
      throw new Error(
        error.response.data.error ||
          `Failed to fetch database mode: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while fetching database mode."
      );
    }
  }
};

/**
 * Switch database mode between production and testing
 * @param {string} mode - The new database mode ('production' or 'testing')
 * @returns {Promise<Object>} Response from the database mode switch
 * @throws {Error} If request fails
 */
export const switchDatabaseMode = async (mode, jwtToken = null) => {
  console.log(`system.js: Switching database mode to ${mode}`); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    // Validate session before making admin API call
    return await withSessionValidation(async () => {
      const response = await axios.post(
        `${getAPI_URL()}/api/database-mode`,
        { mode },
        {
          headers: getJwtHeadersWithToken(jwtToken), // Use JWT-aware headers
        }
      );

      console.log(
        "system.js: Database mode switched successfully:",
        response.data
      ); // DEBUG
      return response.data;
    }, true); // requireAdmin = true
  } catch (error) {
    // Handle validation errors
    if (error.validationResult) {
      console.warn("system.js: Session validation failed:", error.validationResult);
      throw error;
    }

    // Handle other errors
    console.error("system.js: Error switching database mode:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to switch database mode: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while switching database mode."
      );
    }
  }
};

// Admin functions
export const checkAdminStatus = async (jwtToken = null) => {
  console.log("system.js: Checking admin status"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    // Validate session before making admin API call
    return await withSessionValidation(async () => {
      const headers = getJwtHeadersWithToken(jwtToken);
      console.log("system.js: Auth headers:", headers); // DEBUG

      const response = await axios.get(`${getAPI_URL()}/api/admin/check`, {
        headers: headers,
      });

      console.log("system.js: Admin status checked successfully:", response.data); // DEBUG
      return response.data;
    }, true); // requireAdmin = true
  } catch (error) {
    // Handle validation errors
    if (error.validationResult) {
      console.warn("system.js: Session validation failed:", error.validationResult);
      throw error;
    }

    // Handle other errors
    console.error("system.js: Error checking admin status:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to check admin status: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while checking admin status."
      );
    }
  }
};

export const setupTestingDatabase = async () => {
  console.log("system.js: Setting up testing database"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    // Validate session before making admin API call
    return await withSessionValidation(async () => {
      const headers = getAuthHeadersWithJwt();
      console.log("system.js: Auth headers:", headers); // DEBUG

      const response = await axios.post(
        `${getAPI_URL()}/api/admin/setup-testing-db`,
        {},
        { headers: headers }
      );

      console.log("system.js: Testing database setup successful:", response.data); // DEBUG
      return response.data;
    }, true); // requireAdmin = true
  } catch (error) {
    // Handle validation errors
    if (error.validationResult) {
      console.warn("system.js: Session validation failed:", error.validationResult);
      throw error;
    }

    // Handle other errors
    console.error("system.js: Error setting up testing database:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to setup testing database: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while setting up testing database."
      );
    }
  }
};

export const listTestingDatabases = async () => {
  console.log("system.js: Listing testing databases"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  const headers = getAuthHeadersWithJwt();
  console.log("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.get(
      `${getAPI_URL()}/api/admin/list-testing-dbs`,
      {
        headers: headers,
      }
    );

    console.log(
      "system.js: Testing databases listed successfully:",
      response.data
    ); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error listing testing databases:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to list testing databases: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while listing testing databases."
      );
    }
  }
};

export const switchToTestDatabase = async (databaseName) => {
  console.log(`system.js: Switching to test database: ${databaseName}`); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  const headers = getAuthHeadersWithJwt();
  console.log("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.post(
      `${getAPI_URL()}/api/admin/switch-to-test-db`,
      { database_name: databaseName },
      { headers: headers }
    );

    console.log(
      "system.js: Switched to test database successfully:",
      response.data
    ); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error switching to test database:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to switch to test database: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while switching to test database."
      );
    }
  }
};

export const deleteTestDatabase = async (databaseName) => {
  console.log(`system.js: Deleting test database: ${databaseName}`); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  const headers = getAuthHeadersWithJwt();
  console.log("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.delete(
      `${getAPI_URL()}/api/admin/delete-test-db`,
      {
        headers: headers,
        data: { database_name: databaseName },
      }
    );

    console.log(
      "system.js: Test database deleted successfully:",
      response.data
    ); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error deleting test database:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to delete test database: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while deleting test database."
      );
    }
  }
};

/**
 * Get database statistics
 * @returns {Promise<Object>} Database statistics
 * @throws {Error} If request fails
 */
export const getDatabaseStats = async () => {
  console.log("system.js: Getting database statistics"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    // Validate session before making admin API call
    return await withSessionValidation(async () => {
      const headers = getAuthHeadersWithJwt();
      console.log("system.js: Auth headers:", headers); // DEBUG

      const response = await axios.get(`${getAPI_URL()}/api/database-stats`, {
        headers: headers,
      });

      console.log(
        "system.js: Database stats fetched successfully:",
        response.data
      ); // DEBUG
      return response.data;
    }, true); // requireAdmin = true
  } catch (error) {
    // Handle validation errors
    if (error.validationResult) {
      console.warn("system.js: Session validation failed:", error.validationResult);
      throw error;
    }

    // Handle other errors
    console.error("system.js: Error getting database stats:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to get database stats: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while getting database stats."
      );
    }
  }
};

/**
 * Clear database
 * @returns {Promise<Object>} Clear operation result
 * @throws {Error} If request fails
 */
export const clearDatabase = async () => {
  console.log("system.js: Clearing database"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  const headers = getAuthHeadersWithJwt();
  console.log("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.post(
      `${getAPI_URL()}/api/clear-database`,
      {},
      { headers: headers }
    );

    console.log("system.js: Database cleared successfully:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error clearing database:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to clear database: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error("An unexpected error occurred while clearing database.");
    }
  }
};

/**
 * Import sample data
 * @returns {Promise<Object>} Import operation result
 * @throws {Error} If request fails
 */
export const importSampleData = async () => {
  console.log("system.js: Importing sample data"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  const headers = getAuthHeadersWithJwt();
  console.log("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.post(
      `${getAPI_URL()}/api/import-sample-data`,
      {},
      { headers: headers }
    );

    console.log("system.js: Sample data imported successfully:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error importing sample data:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to import sample data: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while importing sample data."
      );
    }
  }
};

/**
 * Import real data
 * @returns {Promise<Object>} Import operation result
 * @throws {Error} If request fails
 */
export const importRealData = async () => {
  console.log("system.js: Importing real data"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  const headers = getAuthHeadersWithJwt();
  console.log("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.post(
      `${getAPI_URL()}/api/import-real-data`,
      {},
      { headers: headers }
    );

    console.log("system.js: Real data imported successfully:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error importing real data:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to import real data: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while importing real data."
      );
    }
  }
};

/**
 * Export data
 * @returns {Promise<Object>} Export operation result
 * @throws {Error} If request fails
 */
export const exportData = async () => {
  console.log("system.js: Exporting data"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  const headers = getAuthHeadersWithJwt();
  console.log("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.post(
      `${getAPI_URL()}/api/export-data`,
      {},
      { headers: headers }
    );

    console.log("system.js: Data exported successfully:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error exporting data:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to export data: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error("An unexpected error occurred while exporting data.");
    }
  }
};

/**
 * Fix JSON data
 * @returns {Promise<Object>} Fix operation result
 * @throws {Error} If request fails
 */
export const fixJsonData = async () => {
  console.log("system.js: Fixing JSON data"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  const headers = getAuthHeadersWithJwt();
  console.log("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.post(
      `${getAPI_URL()}/api/fix-json-data`,
      {},
      { headers: headers }
    );

    console.log("system.js: JSON data fixed successfully:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error fixing JSON data:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to fix JSON data: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error("An unexpected error occurred while fixing JSON data.");
    }
  }
};

/**
 * Get debug engine information
 * @returns {Promise<Object>} Engine info
 * @throws {Error} If request fails
 */
export const getDebugEngineInfo = async () => {
  console.log("system.js: Getting debug engine info"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  const headers = getAuthHeadersWithJwt();
  console.log("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.get(`${getAPI_URL()}/api/debug/engine-info`, {
      headers: headers,
    });

    console.log("system.js: Debug engine info:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error getting debug engine info:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to get debug engine info: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while getting debug engine info."
      );
    }
  }
};

/**
 * Restart the backend server (admin only, development only).
 * @returns {Promise<Object>} Restart response data
 * @throws {Error} If request fails
 */
export const restartBackend = async () => {
  console.log("system.js: Initiating backend restart"); // DEBUG
  console.log("system.js: Using API URL:", getAPI_URL()); // DEBUG

  const headers = getAuthHeadersWithJwt();
  console.log("system.js: Auth headers:", headers); // DEBUG

  try {
    const response = await axios.post(
      `${getAPI_URL()}/api/admin/restart-backend`,
      {},
      { headers: headers }
    );

    console.log(
      "system.js: Backend restart initiated successfully:",
      response.data
    ); // DEBUG
    return response.data;
  } catch (error) {
    console.error("system.js: Error initiating backend restart:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to restart backend: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while restarting the backend."
      );
    }
  }
};
